import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  MouseEvent,
  ReactNode,
} from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { cx } from '../cx'
import { Spinner } from '../Loading/Loading'
import styles from './Button.module.css'

type Common = {
  variant?: 'primary' | 'danger' | 'outline' | 'ghost' | 'quiet'
  size?: 'sm' | 'md' | 'icon'
  /** `surface` (a card, an overlay) hovers and presses to raise. */
  ground?: 'page' | 'surface'
  children: ReactNode
}

type AsButton = ButtonHTMLAttributes<HTMLButtonElement> &
  Common & {
    href?: undefined
    loading?: boolean
    active?: boolean
  }

/** Navigates: a real router link in button paint (D186). */
type AsLink = Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'href' | 'target' | 'rel' | 'title'
> &
  Common & {
    href: string
    state?: unknown
    replace?: boolean
  }

export type ButtonProps = AsButton | AsLink

export function Button(props: ButtonProps) {
  if (props.href !== undefined) {
    const {
      variant = 'primary',
      size = 'md',
      ground = 'page',
      className,
      children,
      href,
      state,
      replace,
      ...rest
    } = props
    return (
      <RouterLink
        {...rest}
        to={href}
        state={state}
        replace={replace}
        className={cx(styles.button, className)}
        data-variant={variant}
        data-size={size}
        data-ground={ground}
      >
        <span className={styles.content}>{children}</span>
      </RouterLink>
    )
  }
  return <ButtonElement {...props} />
}

function ButtonElement({
  variant = 'primary',
  size = 'md',
  ground = 'page',
  type = 'button',
  loading,
  active,
  className,
  onClick,
  children,
  ...rest
}: AsButton) {
  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    if (loading) {
      event.preventDefault()
      return
    }
    onClick?.(event)
  }

  return (
    <button
      {...rest}
      type={type}
      className={cx(styles.button, className)}
      data-variant={variant}
      data-size={size}
      data-ground={ground}
      aria-pressed={active}
      aria-busy={loading || undefined}
      aria-disabled={loading || rest['aria-disabled'] || undefined}
      onClick={handleClick}
    >
      <span className={styles.content}>
        {loading && <Spinner tone="current" />}
        {children}
      </span>
    </button>
  )
}
